<template>
  <div class="relative w-full max-w-7xl mx-auto mt-20 mb-12 overflow-hidden">
    <!-- Header with stylized text and animated background shapes -->
    <div class="relative flex justify-center md:justify-end px-4 md:pl-12 mb-6">
      <div class="absolute -top-16 -right-20 w-64 h-64 bg-gradient-to-br from-primary/30 to-transparent rounded-full blur-2xl animate-float"></div>
      <div class="absolute top-10 left-20 w-40 h-40 bg-gradient-to-bl from-secondary/20 to-transparent rounded-full blur-xl animate-float-slow"></div>
      
      <h2 class="relative text-4xl md:text-6xl font-bold text-white z-10">
        Parts <span class="text-secondary font-bold text-shadow-lg">&</span> Service
      </h2>
    </div>
    
    <!-- Modern card design with overlapping elements -->
    <div class="relative flex flex-col md:flex-row items-center gap-6 px-6 md:px-12">
      <!-- Decorative shapes -->
      <div class="absolute -bottom-24 -right-24 w-96 h-96 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-2xl"></div>
      <div class="absolute top-1/3 -left-32 w-64 h-64 bg-gradient-to-tl from-secondary/10 to-transparent rounded-full blur-3xl"></div>
      
      
      
      <!-- Content card with glass morphism -->
      <div 
        class="relative w-full md:w-3/5 p-6 md:p-8 mt-6 md:mt-0 rounded-xl backdrop-blur-sm bg-base-300/40 
               border border-white/10 shadow-xl overflow-hidden group"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
      >
        <!-- Animated gradient background -->
        <div class="absolute inset-0 bg-gradient-to-br from-base-100/70 via-base-200/50 to-base-300/70 z-0"></div>
        <div 
          class="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-secondary/20 
                 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-0"
        ></div>
        
        <!-- Text content with animations -->
        <div class="relative z-10 space-y-4">
          <p 
            class="text-xl md:text-2xl font-semibold text-white leading-relaxed transform transition-all duration-500"
            :class="{'translate-y-0 opacity-100': isVisible, '-translate-y-4 opacity-0': !isVisible}"
          >
            STE is committed to keeping your costs down and your equipment running at optimum levels.
          </p>
          
          <p 
            class="text-base md:text-lg text-white/90 leading-relaxed transform transition-all duration-500 delay-200"
            :class="{'translate-y-0 opacity-100': isVisible, '-translate-y-4 opacity-0': !isVisible}"
          >
            We have precision manufactured parts and experienced service techs to ensure that your tank trailers continue to provide your business with reliable assets.
          </p>
          
          <!-- Interactive call-to-action button -->
          <div 
            class="inline-flex items-center gap-2 mt-6 px-5 py-3 bg-gradient-to-r from-primary to-primary/80 
                   rounded-full text-white font-semibold cursor-pointer overflow-hidden 
                   transform transition-all duration-500 hover:shadow-lg hover:shadow-primary/30 
                   group-hover:translate-y-0 hover:scale-105"
            :class="{'translate-y-0 opacity-100': isVisible, 'translate-y-8 opacity-0': !isVisible}"
            @click="navigateToPartsServicePage"
          >
            <span class="relative z-10">Explore Services</span>
            <font-awesome-icon 
              :icon="['fas', 'right-long']" 
              class="relative z-10 transition-transform duration-300 group-hover:translate-x-1" 
            />
            
            <!-- Button hover effect -->
            <div class="absolute inset-0 bg-primary/80 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
          </div>
        </div>
        
        <!-- Decorative corner accents -->
        <div class="absolute top-0 right-0 w-20 h-20 border-t-2 border-r-2 border-white/20 rounded-tr-xl"></div>
        <div class="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-primary/30 rounded-bl-xl"></div>
      </div>
      
      <!-- Image section with parallax effect -->
      <div 
        class="relative w-full md:w-2/5 aspect-square md:aspect-auto md:h-full rounded-xl"
        ref="imageContainer"
        @mousemove="handleParallax"
        @mouseleave="resetParallax"
      >
        <div class="absolute inset-0 z-10"></div>
        
        <!-- Background shape -->
        <div class="absolute -bottom-8 -right-8 w-40 h-40 bg-primary/30 rounded-full blur-md z-0 animate-pulse"></div>
        
        <!-- Main image with parallax effect -->
        <img 
          src="/img/WeldingFront.png" 
          alt="Welding Front" 
          class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out z-0 overflow-visible brightness-[0.8]"
          ref="parallaxImage"
        />
        
        <!-- Overlapping geometric elements -->
        <div class="absolute top-6 -right-3 w-24 h-24 border-2 border-primary/30 rounded-lg rotate-12 z-20"></div>
        <div class="absolute bottom-10 left-10 w-20 h-20 bg-secondary/10 rounded-full backdrop-blur-sm z-20 animate-float-slow"></div>
        
        <!-- Highlight accent -->
        <div class="absolute top-0 right-0 w-full h-full opacity-60 z-10"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartsAndService',
  data() {
    return {
      isHovered: false,
      isVisible: false,
      mouseX: 0,
      mouseY: 0
    };
  },
  mounted() {
    // Trigger entrance animations
    setTimeout(() => {
      this.isVisible = true;
    }, 300);
    
    // Add intersection observer for scroll animations
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    // Clean up any observers
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    navigateToPartsServicePage() {
      // Navigate to the new Parts & Service page
      this.isHovered = false;
      setTimeout(() => {
        this.$router.push('/parts-service');
      }, 300);
    },
    
    handleParallax(e) {
      const container = this.$refs.imageContainer;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Calculate movement based on mouse position
      const moveX = (x - rect.width / 2) / 20;
      const moveY = (y - rect.height / 2) / 20;
      
      // Apply parallax effect to image
      if (this.$refs.parallaxImage) {
        this.$refs.parallaxImage.style.transform = `translate(${-moveX}px, ${-moveY}px) scale(1.1)`;
      }
    },
    
    resetParallax() {
      if (this.$refs.parallaxImage) {
        this.$refs.parallaxImage.style.transform = 'translate(0, 0) scale(1.05)';
      }
    },
    
    setupIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.isVisible = true;
            } else {
              this.isVisible = false;
            }
          });
        }, { threshold: 0.2 });
        
        this.observer.observe(this.$el);
      } else {
        // Fallback for browsers that don't support IntersectionObserver
        this.isVisible = true;
      }
    }
  }
};
</script>

<style scoped>
/* Base animations */
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0); }
  50% { transform: translateY(-10px) rotate(2deg); }
}

@keyframes float-slow {
  0%, 100% { transform: translateY(0) rotate(0); }
  50% { transform: translateY(-15px) rotate(-3deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 0.9; }
}

/* Animation classes */
.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-slow {
  animation: float-slow 12s ease-in-out infinite;
}

.animate-pulse {
  animation: pulse 5s ease-in-out infinite;
}

/* Text shadow */
.text-shadow-lg {
  text-shadow: 3px 3px 0px #000;
}

/* Image container styling */
.aspect-square {
  aspect-ratio: 1 / 1;
}

/* Transitions for interactive elements */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Media queries for responsive design */
@media (max-width: 768px) {
  .animate-float, .animate-float-slow {
    animation-duration: 15s;
  }
}

/* Improve touch device interactions */
@media (hover: none) {
  .group:active .group-hover\:opacity-100 {
    opacity: 1;
  }
  
  .group:active .group-hover\:translate-y-0 {
    transform: translateY(0);
  }
  
  .group:active .group-hover\:scale-x-100 {
    transform: scaleX(1);
  }
}
</style>
